<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="announcements-page">
    <div class="page-header">
      <div class="page-avatar"><v-icon size="22" color="#fff">mdi-bullhorn</v-icon></div>
      <div class="page-header-text">
        <h1 class="page-title">Ogłoszenia</h1>
      </div>
      <button class="action-pill action-pill--primary" @click="openDialog()">
        <v-icon size="14">mdi-plus</v-icon>{{ $t('announcements.addButton') }}
      </button>
    </div>
    <v-card>
      <v-card-title class="text-h5 primary--text font-weight-bold">
        <v-icon left color="primary">mdi-bullhorn</v-icon>
        {{ $t('announcements.title') }}
      </v-card-title>

      <v-card-text>
        <v-text-field v-model="search" append-icon="mdi-magnify" :label="$t('common.search') + '...'" single-line hide-details dense outlined class="mb-4" style="max-width: 300px" />

        <v-data-table
          :headers="computedHeaders"
          :items="announcements"
          :loading="loading"
          :search="search"
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [10, 25, 50], 'items-per-page-text': $t('common.perPage') }"
          class="elevation-1"
          :loading-text="$t('common.loading')"
          :no-data-text="$t('common.noData')"
        >
          <template v-slot:item.contentPreview="{ item }">
            <span style="max-width: 300px; display: inline-block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ stripHtml(item.content) }}</span>
          </template>
          <template v-slot:item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>
          <template v-slot:item.expiresAt="{ item }">
            <span v-if="item.expiresAt">{{ formatDateTime(item.expiresAt) }}</span>
            <span v-else class="grey--text">{{ $t('announcements.noExpiry') }}</span>
          </template>
          <template v-slot:item.targets="{ item }">
            <v-chip v-if="!item.targetSubCompanyIds.length" x-small color="primary" text-color="white">{{ $t('announcements.allTargets') }}</v-chip>
            <template v-else>
              <v-chip v-for="scId in item.targetSubCompanyIds" :key="'sc-'+scId" x-small class="mr-1 mb-1" color="teal" text-color="white">{{ getSubCompanyName(scId) }}</v-chip>
            </template>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="isActive(item) ? 'success' : 'grey'" text-color="white">
              {{ isActive(item) ? $t('announcements.statusActive') : $t('announcements.statusExpired') }}
            </v-chip>
          </template>
          <template v-slot:item.author="{ item }">
            {{ item.createdBy ? item.createdBy.firstName + ' ' + item.createdBy.lastName : '—' }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn small icon color="info" @click="openDialog(item)" class="mr-1"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn small icon color="error" @click="deleteAnnouncement(item.id)"><v-icon small>mdi-delete</v-icon></v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="dialog" max-width="700" scrollable>
      <v-card>
        <v-card-title>{{ editingId ? $t('announcements.editTitle') : $t('announcements.newTitle') }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field v-model="form.title" :label="$t('announcements.titleLabel')" outlined class="mb-3" />
            <div class="text-subtitle-2 mb-2">{{ $t('announcements.contentLabel') }}</div>
            <div class="announcement-editor mb-3">
              <vue-editor ref="editor" v-model="form.content" :editorToolbar="editorToolbar" />
              <div class="emoji-bar pa-2" style="background: #2A3A50; border: 1px solid #37474F; border-top: none; border-radius: 0 0 4px 4px;">
                <span
                  v-for="emoji in emojis"
                  :key="emoji"
                  class="emoji-btn"
                  @click="insertEmoji(emoji)"
                >{{ emoji }}</span>
              </div>
            </div>

            <v-row dense>
              <v-col cols="7">
                <v-text-field v-model="form.expiresDate" :label="$t('announcements.expiresDateLabel')" outlined type="date" clearable />
              </v-col>
              <v-col cols="5">
                <v-text-field v-model="form.expiresTime" :label="$t('announcements.expiresTimeLabel')" outlined type="time" clearable :disabled="!form.expiresDate" />
              </v-col>
            </v-row>

            <v-checkbox v-model="form.sendToAll" :label="$t('announcements.sendToAll')" hide-details class="mb-3" />

            <v-select
              v-if="!form.sendToAll"
              v-model="form.targetSubCompanyIds"
              :items="availableSubCompanies"
              item-text="name"
              item-value="id"
              :label="$t('announcements.subCompaniesLabel')"
              outlined
              multiple
              chips
              small-chips
              deletable-chips
              :rules="[v => form.sendToAll || (v && v.length > 0) || 'Wybierz oddział']"
              :hint="isLimitedToSubCompanies ? $t('announcements.limitedAccess') : ''"
              persistent-hint
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :disabled="!valid" :loading="saving" @click="save">
            {{ editingId ? $t('common.save') : $t('common.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { VueEditor } from 'vue2-editor';

export default {
  name: 'AnnouncementsPage',
  components: { VueEditor },
  data() {
    return {
      loading: false,
      search: '',
      announcements: [],
      subCompanies: [],
      adminSubCompanyIds: [],
      dialog: false,
      valid: false,
      saving: false,
      editingId: null,
      form: {
        title: '',
        content: '',
        expiresDate: null,
        expiresTime: null,
        sendToAll: true,
        targetSubCompanyIds: [],
      },
      emojis: [
        '😀', '😂', '🥳', '👍', '👏', '🎉', '🔥', '⚠️', '❗', '✅',
        '❌', '📢', '📌', '💰', '🚗', '🛞', '⛽', '🔧', '📋', '📅',
        '⏰', '🏆', '💪', '🙏', '👋', '🎯', '💡', '📣', '🚨', '✨',
      ],
      editorToolbar: [
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ script: 'sub' }, { script: 'super' }],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }, { align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      headers: [],
    };
  },
  computed: {
    computedHeaders() {
      return [
        { text: this.$t('announcements.headers.title'), value: 'title', sortable: true },
        { text: this.$t('announcements.headers.content'), value: 'contentPreview', sortable: false },
        { text: this.$t('announcements.headers.createdAt'), value: 'createdAt', sortable: true },
        { text: this.$t('announcements.headers.expiresAt'), value: 'expiresAt', sortable: true },
        { text: this.$t('announcements.headers.targets'), value: 'targets', sortable: false },
        { text: this.$t('announcements.headers.status'), value: 'status', sortable: false },
        { text: this.$t('announcements.headers.author'), value: 'author', sortable: false },
        { text: '', value: 'actions', sortable: false, width: '100px' },
      ];
    },
    isLimitedToSubCompanies() {
      return this.adminSubCompanyIds.length > 0;
    },
    availableSubCompanies() {
      if (!this.isLimitedToSubCompanies) return this.subCompanies;
      return this.subCompanies.filter(s => this.adminSubCompanyIds.includes(s.id));
    },
  },
  async created() {
    await Promise.all([this.fetchAnnouncements(), this.fetchSubCompanies(), this.fetchAdminProfile()]);
  },
  methods: {
    getConfig() {
      return { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
    },
    async fetchAnnouncements() {
      this.loading = true;
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'announcements', ...this.getConfig() });
        this.announcements = res.data?.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async fetchAdminProfile() {
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'company-admins/me', ...this.getConfig() });
        const data = res.data?.data;
        if (data?.subcompanies && data.subcompanies.length > 0) {
          this.adminSubCompanyIds = data.subcompanies.map(s => s.subcompanyId);
        }
      } catch (e) {
        console.error(e);
      }
    },
    async fetchSubCompanies() {
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'sub-company', ...this.getConfig() });
        this.subCompanies = (res.data?.data || []).map(s => ({ id: s.id, name: s.name }));
      } catch (e) {
        console.error(e);
      }
    },
    insertEmoji(emoji) {
      const editor = this.$refs.editor?.quill;
      if (editor) {
        const range = editor.getSelection(true);
        editor.insertText(range.index, emoji);
        editor.setSelection(range.index + emoji.length);
      } else {
        this.form.content = (this.form.content || '') + emoji;
      }
    },
    openDialog(item) {
      if (item) {
        this.editingId = item.id;
        const exp = item.expiresAt ? new Date(item.expiresAt) : null;
        this.form = {
          title: item.title || '',
          content: item.content,
          expiresDate: exp ? exp.toISOString().substring(0, 10) : null,
          expiresTime: exp ? exp.toTimeString().substring(0, 5) : null,
          sendToAll: item.targetSubCompanyIds.length === 0,
          targetSubCompanyIds: [...item.targetSubCompanyIds],
        };
      } else {
        this.editingId = null;
        this.form = { title: '', content: '', expiresDate: null, expiresTime: null, sendToAll: true, targetSubCompanyIds: [] };
      }
      this.dialog = true;
    },
    async save() {
      this.saving = true;
      try {
        let expiresAt = null;
        if (this.form.expiresDate) {
          const time = this.form.expiresTime || '23:59';
          expiresAt = `${this.form.expiresDate}T${time}:00`;
        }
        const payload = {
          title: this.form.title || '-',
          content: this.form.content,
          expiresAt,
          targetCities: [],
          targetSubCompanyIds: this.form.sendToAll ? [] : this.form.targetSubCompanyIds,
        };
        if (this.editingId) {
          payload.id = this.editingId;
          await axios({ method: 'put', url: process.env.VUE_APP_ROOT_API + 'announcements', data: payload, ...this.getConfig() });
        } else {
          await axios({ method: 'post', url: process.env.VUE_APP_ROOT_API + 'announcements', data: payload, ...this.getConfig() });
        }
        this.dialog = false;
        await this.fetchAnnouncements();
        Swal.fire({ title: this.$t('common.success'), icon: 'success', timer: 1500, showConfirmButton: false });
      } catch (e) {
        console.error(e);
        Swal.fire(this.$t('common.error'), e.response?.data?.message || this.$t('common.error'), 'error');
      } finally {
        this.saving = false;
      }
    },
    async deleteAnnouncement(id) {
      const result = await Swal.fire({
        title: this.$t('announcements.confirmDelete'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#F44336',
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
      });
      if (!result.isConfirmed) return;
      try {
        await axios({ method: 'delete', url: process.env.VUE_APP_ROOT_API + 'announcements/' + id, ...this.getConfig() });
        await this.fetchAnnouncements();
      } catch (e) {
        Swal.fire(this.$t('common.error'), this.$t('common.error'), 'error');
      }
    },
    isActive(item) {
      if (!item.expiresAt) return true;
      return new Date(item.expiresAt) >= new Date();
    },
    getSubCompanyName(id) {
      const sc = this.subCompanies.find(s => s.id === id);
      return sc ? sc.name : id;
    },
    stripHtml(html) {
      if (!html) return '';
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('pl-PL', { year: 'numeric', month: '2-digit', day: '2-digit' });
    },
    formatDateTime(d) {
      if (!d) return '—';
      const date = new Date(d);
      const dateStr = date.toLocaleDateString('pl-PL', { year: 'numeric', month: '2-digit', day: '2-digit' });
      const timeStr = date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
      return timeStr === '23:59' ? dateStr : `${dateStr} ${timeStr}`;
    },
  },
};
</script>

<style scoped>
.announcements-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { display: flex; align-items: center; gap: 12px; padding: 4px 4px 18px; }
.page-avatar { width: 44px; height: 44px; border-radius: 14px; background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 6px 18px rgba(41,98,255,0.4); }
.page-header-text { flex: 1; }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; text-transform: capitalize; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.action-pill { display: inline-flex; align-items: center; gap: 6px; background: #2962FF; border: 1px solid #2962FF; color: #fff; padding: 8px 14px; border-radius: 10px; font-size: 12.5px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 12px rgba(41,98,255,0.3); transition: transform 0.12s ease; font-family: inherit; }
.action-pill:hover { transform: translateY(-1px); }
.action-pill .v-icon { color: inherit !important; }

.announcements-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.announcements-page >>> .v-card .v-card__title { color: #fff !important; padding: 16px 20px 8px !important; font-weight: 700 !important; font-size: 15px !important; }
.announcements-page >>> .v-card .v-card__title .v-icon { color: #5478FF !important; }
.announcements-page >>> .v-card .primary--text { color: #fff !important; }
.announcements-page >>> .v-text-field--outlined fieldset, .announcements-page >>> .v-select--outlined fieldset, .announcements-page >>> .v-textarea--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.announcements-page >>> .v-text-field--outlined .v-input__slot, .announcements-page >>> .v-select--outlined .v-input__slot, .announcements-page >>> .v-textarea--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.announcements-page >>> .v-label { color: #6E7691 !important; }
.announcements-page >>> .v-text-field input, .announcements-page >>> .v-select__selection, .announcements-page >>> .v-textarea textarea { color: #fff !important; }
.announcements-page >>> .v-data-table { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 14px !important; color: #fff !important; box-shadow: none !important; }
.announcements-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.announcements-page >>> .v-data-table tbody tr { background: #232838 !important; }
.announcements-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.announcements-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.announcements-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.announcements-page >>> .v-btn:not(.v-btn--icon):not(.action-pill) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.announcements-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.announcements-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.announcements-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.announcements-page >>> .v-dialog .v-card { background: #232838 !important; }
</style>

<style>
.announcement-editor .ql-toolbar {
  background: #2A3A50 !important;
  border-color: #37474F !important;
  border-radius: 4px 4px 0 0;
}
.announcement-editor .ql-toolbar .ql-stroke {
  stroke: #B0BEC5 !important;
}
.announcement-editor .ql-toolbar .ql-fill {
  fill: #B0BEC5 !important;
}
.announcement-editor .ql-toolbar .ql-picker-label {
  color: #B0BEC5 !important;
}
.announcement-editor .ql-toolbar button:hover .ql-stroke,
.announcement-editor .ql-toolbar .ql-active .ql-stroke {
  stroke: #fff !important;
}
.announcement-editor .ql-toolbar button:hover .ql-fill,
.announcement-editor .ql-toolbar .ql-active .ql-fill {
  fill: #fff !important;
}
.announcement-editor .ql-toolbar button:hover,
.announcement-editor .ql-toolbar .ql-active {
  background: rgba(255,255,255,0.1) !important;
  border-radius: 3px;
}
.announcement-editor .ql-container {
  background: #1E2D3F !important;
  border-color: #37474F !important;
  border-radius: 0 0 4px 4px;
  color: #fff !important;
  min-height: 150px;
}
.announcement-editor .ql-editor {
  color: #fff !important;
  min-height: 150px;
}
.announcement-editor .ql-editor.ql-blank::before {
  color: #546E7A !important;
}
.announcement-editor .ql-picker-options {
  background: #1E2D3F !important;
  border-color: #37474F !important;
}
.announcement-editor .ql-picker-item {
  color: #B0BEC5 !important;
}
.announcement-editor .ql-picker-item:hover {
  color: #fff !important;
}
.announcement-editor .ql-snow .ql-tooltip {
  background: #1E2D3F !important;
  border-color: #37474F !important;
  color: #fff !important;
}
.announcement-editor .ql-snow .ql-tooltip input[type=text] {
  background: #2A3A50 !important;
  border-color: #37474F !important;
  color: #fff !important;
}
.emoji-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}
.emoji-btn {
  cursor: pointer;
  font-size: 20px;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.15s;
  line-height: 1;
}
.emoji-btn:hover {
  background: rgba(255,255,255,0.1);
}
</style>
